<template>
  <b-modal
    v-model="isVisible"
    title="Send notification"
    size="lg"
    centered
    :hide-footer="false"
    modal-class="mass-notification-modal"
    @show="onMassNotificationModalShow"
    @hidden="onMassNotificationModalHidden"
  >
    <div v-if="showInitialSpinner" class="text-center py-5">
      <div class="text-center text-primary my-2">
        <digger-loader />
      </div>
      <p class="mt-2 text-muted">Loading projects and tradies...</p>
    </div>
    <b-overlay
      v-else
      :show="isLocked"
      rounded="sm"
      opacity="0.5"
      class="mass-notif-lock-overlay"
    >
      <template #overlay>
        <div class="text-center">
          <img src="@/assets/images/lock.png" alt="Locked" class="lock-icon" />
          <h2 class="font-weight-bold mb-0">Assign tokens to use</h2>
          <h4 class="text-muted mb-4">
            You need at least one tradie assigned to send notifications.
          </h4>
        </div>
      </template>

      <b-overlay :show="massNotificationSending" rounded="sm">
        <template #overlay>
          <div class="text-center">
            <digger-loader />
          </div>
        </template>
        <b-tabs v-model="massNotificationTab" class="mb-3">
          <b-tab v-if="showTradiesTab" title="Tradies">
            <!-- 1) Search -->
            <b-form-input
              v-model.trim="tradieSearch"
              debounce="350"
              placeholder="Search tradie by name/email"
              class="mb-2 mt-2"
            />

            <!-- 2) Select all row, aligned with tradies -->
            <div
              class="mass-notif-select-all d-flex align-items-center w-100 mass-notif-row mass-notif-row--user"
            >
              <b-form-checkbox
                :indeterminate="someTradiesSelected && !allTradiesSelected"
                :checked="allTradiesSelected"
                :disabled="companyTradiesSelectAllBusy"
                @change="toggleSelectAllTradies"
                class="mass-notif-checkbox"
              >
                &nbsp;
              </b-form-checkbox>
              <span class="mass-notif-label">Select all</span>
            </div>

            <!-- 3) Divider -->
            <hr class="mass-notif-divider" />

            <!-- 4) Tradies list -->
            <div
              class="mass-notif-list mass-notif-list--tradies"
              style="max-height: 50vh; overflow-y: auto"
            >
              <div
                v-for="t in sortedCompanyTradies"
                :key="'tradie-' + t.id"
                class="d-flex align-items-center w-100 mass-notif-row mass-notif-row--user"
                :id="'tooltip-target-' + t.id"
              >
                <b-form-checkbox
                  v-if="t.can_access"
                  :checked="isTradieSelected(t.id)"
                  @change="(checked) => toggleTradieSelection(t.id, checked)"
                  class="mass-notif-checkbox"
                >
                  &nbsp;
                </b-form-checkbox>

                <span
                  v-else
                  class="mass-notif-checkbox d-flex align-items-center"
                  style="padding-left: 4.3px"
                >
                  <b-icon-lock />
                </span>

                <span
                  class="mass-notif-label"
                  :class="{ 'text-muted': !t.can_access }"
                >
                  {{ t.full_name || t.email }}
                </span>
                <b-tooltip
                  :target="'tooltip-target-' + t.id"
                  triggers="hover"
                  delay="0"
                >
                  <div v-if="!t.can_access">Locked - Assign token to view</div>
                </b-tooltip>
              </div>

              <div
                v-if="companyTradiesLoading"
                class="text-center py-2 text-muted"
              >
                Loading tradies...
              </div>

              <div
                v-else-if="
                  !companyTradiesLoading &&
                  !companyTradiesLoadingMore &&
                  sortedCompanyTradies.length === 0
                "
                class="py-2 text-muted small pl-3"
              >
                No tradies found
              </div>
              <div v-if="companyTradiesHasMore" class="text-center py-2">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  :disabled="companyTradiesLoadingMore"
                  @click="loadMoreCompanyTradies"
                >
                  {{
                    companyTradiesLoadingMore
                      ? 'Loading...'
                      : 'Load more tradies'
                  }}
                </b-button>
              </div>
            </div>
          </b-tab>
          <b-tab v-if="showProjectsTab" title="Projects">
            <b-form-input
              v-model.trim="projectSearch"
              placeholder="Search project by name"
              class="mb-2 mt-2"
            />

            <div
              class="mass-notif-list mass-notif-list--projects"
              style="max-height: 50vh; overflow-y: auto"
            >
              <div
                v-for="project in paginatedProjects"
                :key="'proj-' + project.id"
              >
                <div
                  :id="'proj-row-' + project.id"
                  class="d-flex align-items-center w-100 mass-notif-row mass-notif-row--project"
                >
                  <b-form-checkbox
                    :checked="massNotificationProjectIds.includes(project.id)"
                    :disabled="projectHasNoTokenUsers(project.id)"
                    @change="
                      (checked) => toggleProjectSelection(project.id, checked)
                    "
                    class="mass-notif-checkbox"
                    :class="{
                      'mass-notif-checkbox--disabled': projectHasNoTokenUsers(
                        project.id,
                      ),
                    }"
                    @click.native.stop
                  >
                    &nbsp;
                  </b-form-checkbox>

                  <b-button
                    squared
                    v-b-toggle="`mass-notif-collapse-${project.id}`"
                    block
                    variant="primary"
                    class="mass-notif-toggle-btn"
                    @click="onProjectExpand(project)"
                  >
                    <div
                      class="d-flex justify-content-between align-items-center w-100"
                    >
                      <span>{{ project.name || `Project ${project.id}` }}</span>
                      <b-icon icon="chevron-right" class="mass-notif-chevron" />
                    </div>
                  </b-button>
                </div>

                <b-tooltip
                  v-if="projectHasNoTokenUsers(project.id)"
                  :target="'proj-row-' + project.id"
                  triggers="hover"
                  delay="0"
                >
                  No tradies with tokens on this project
                </b-tooltip>

                <b-collapse :id="`mass-notif-collapse-${project.id}`">
                  <div
                    v-for="t in tradiesOnProject(project.id)"
                    :key="'user-' + project.id + '-' + t.id"
                    class="d-flex align-items-center w-100 mass-notif-row mass-notif-row--user"
                    :id="'proj-tooltip-' + project.id + t.id"
                  >
                    <b-form-checkbox
                      v-if="t.can_access"
                      :checked="
                        (
                          massNotificationSelectedByProject[project.id] || []
                        ).includes(t.id)
                      "
                      @change="
                        (checked) =>
                          toggleMassNotificationTradie(
                            project.id,
                            t.id,
                            checked,
                          )
                      "
                      class="mass-notif-checkbox"
                    >
                      &nbsp;
                    </b-form-checkbox>

                    <span
                      v-else
                      class="mass-notif-checkbox d-flex align-items-center"
                      style="padding-left: 4.3px"
                    >
                      <b-icon-lock />
                    </span>

                    <span
                      class="mass-notif-label"
                      :class="{ 'text-muted': !t.can_access }"
                    >
                      {{ t.full_name || t.email }}
                    </span>
                    <b-tooltip
                      :target="'proj-tooltip-' + project.id + t.id"
                      triggers="hover"
                      delay="0"
                    >
                      <div v-if="!t.can_access">
                        Locked - Assign token to view
                      </div>
                    </b-tooltip>
                  </div>

                  <!-- Pagination within in the individual project-->
                  <div>
                    <b-pagination
                      v-if="projectTradieRows(project.id) > perPage"
                      :value="tradiesOnProjectPage[project.id] || 1"
                      @input="(page) => onProjectTradiePage(project.id, page)"
                      :total-rows="projectTradieRows(project.id)"
                      :per-page="perPage"
                      align="center"
                      class="mt-2"
                    />
                  </div>

                  <div
                    v-if="projectTradiesHasMorePages(project.id)"
                    class="text-center py-2"
                  >
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="projectTradiesLoading[project.id]"
                      @click="loadMoreProjectTradies(project.id)"
                    >
                      Load more tradies
                    </b-button>
                  </div>

                  <div
                    v-if="projectTradiesLoading[project.id]"
                    class="text-center py-2 text-muted"
                  >
                    Loading tradies...
                  </div>

                  <div
                    v-else-if="
                      tradiesOnProject(project.id).length === 0 &&
                      projectTradiesCache[project.id]
                    "
                    class="py-2 text-muted small pl-3"
                  >
                    No tradies on this project
                  </div>
                </b-collapse>
              </div>
            </div>
            <b-pagination
              v-if="projectRows > perPage"
              v-model="projectPage"
              :total-rows="projectRows"
              :per-page="perPage"
              align="center"
              class="mt-2"
            />
          </b-tab>
        </b-tabs>

        <p class="mt-2 mb-0">
          <span
            v-if="
              activeTabIsProjects &&
              (massNotificationProjectIds.length ||
                individuallySelectedUserIds.length)
            "
          >
            <template v-if="massNotificationProjectIds.length">
              {{ massNotificationProjectIds.length }} project(s)
            </template>
            <template
              v-if="
                massNotificationProjectIds.length &&
                individuallySelectedUserIds.length
              "
            >
              and
            </template>
            <template v-if="individuallySelectedUserIds.length">
              {{ individuallySelectedUserIds.length }} tradie(s)
            </template>
            selected.
          </span>
          <span
            v-if="
              !activeTabIsProjects && massNotificationSelectedUserIds.length
            "
          >
            {{ massNotificationSelectedUserIds.length }} tradie(s) selected.
          </span>
          <span
            v-if="
              (activeTabIsProjects &&
                !massNotificationProjectIds.length &&
                !individuallySelectedUserIds.length) ||
              (!activeTabIsProjects && !massNotificationSelectedUserIds.length)
            "
            class="text-muted"
          >
            Select at least one recipient.
          </span>
        </p>

        <b-form-group label="Title" required class="mt-3">
          <b-form-input v-model="massNotificationTitle" required />
        </b-form-group>

        <b-form-group label="Body" required>
          <b-form-textarea v-model="massNotificationBody" required rows="3" />
        </b-form-group>

        <div>
          <h5><strong>File Upload</strong></h5>
          <div
            class="upload-container dashed mb-4 upload-border"
            v-cloak
            @drop.prevent="loadPhotos($event)"
            @dragover.prevent
          >
            <b-img
              :src="require('@/assets/icons/upload.svg')"
              alt="Photos"
              class="mt-4"
              v-bind="mainProps"
            ></b-img>

            <div class="upload-label mt-3 mb-3">
              <b-button variant="primary" @click="launchFilePicker">
                Upload from device
              </b-button>
            </div>

            <b-form-file
              ref="filePicker"
              multiple
              accept="image/jpeg, image/png, .pdf, .csv, .xlsx"
              style="display: none"
              @input="loadPhotos($event)"
              :state="state.files"
              aria-invalid="input-files-feedback"
            ></b-form-file>
          </div>

          <div class="p-2 text-danger small" v-show="state.files === false">
            {{ errors.files }}
          </div>

          <div class="preview-container" v-if="previews.length !== 0">
            <div
              class="preview-item"
              :class="pIndex !== previews.length - 1 ? 'mr-3' : ''"
              v-for="(preview, pIndex) in previews"
              :key="pIndex"
            >
              <b-icon-x-circle-fill
                variant="dark"
                class="remove-icon"
                @click="onRemovePreview(pIndex)"
              ></b-icon-x-circle-fill>

              <!-- previewProps carries `blank: true`, which makes b-img/
                   b-img-lazy discard `src` entirely and render a generated
                   placeholder instead — correct for the lazy-loading photo
                   thumbnails it was written for (blank until scrolled into
                   view, then swapped), but it silently blanks out the
                   pdf/csv/xlsx bundled icons too since those never go
                   through a lazy swap. Icons get a plain b-img with no
                   previewProps so their `src` is actually respected. -->
              <b-img
                v-if="
                  preview === 'pdf' || preview === 'csv' || preview === 'xlsx'
                "
                :src="
                  preview === 'pdf'
                    ? require('@/assets/icons/adobe-acrobat.svg')
                    : preview === 'csv'
                    ? require('@/assets/icons/csv-file.svg')
                    : require('@/assets/icons/xlsx-file.svg')
                "
                alt="Photos"
                center
                class="h-100"
              ></b-img>
              <b-img-lazy
                v-else
                :src="preview"
                alt="Photos"
                v-bind="previewProps"
                class="h-100"
              ></b-img-lazy>
            </div>
          </div>
        </div>

        <b-alert
          v-if="massNotificationError"
          variant="danger"
          show
          class="mb-0 mt-2 d-flex align-items-center"
        >
          <b-icon-exclamation-triangle-fill class="mr-2 flex-shrink-0" />
          <span>{{ massNotificationError }}</span>
        </b-alert>
      </b-overlay>
    </b-overlay>
    <template #modal-footer>
      <div class="mass-notification-footer">
        <b-button
          variant="outline-secondary"
          class="mass-notification-btn"
          @click="close"
          >Cancel</b-button
        >
        <b-button
          variant="primary"
          class="mass-notification-btn"
          :disabled="!canSendMassNotification || massNotificationSending"
          @click="onSendMassNotification"
        >
          Send
        </b-button>
      </div>
    </template>
  </b-modal>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import Compressor from 'compressorjs'
import Api from '@/api'

export default {
  name: 'MassNotificationModal',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      massNotificationProjectIds: [],
      massNotificationSelectedProjectId: null,
      // Per-project selection: { [projectId]: number[] } so a user selected in one project is not selected in others
      massNotificationSelectedByProject: {},
      massNotificationTitle: '',
      massNotificationBody: '',
      massNotificationError: null,
      massNotificationSending: false,
      massNotificationByProjectRecipientCount: 0,
      projectTradiesCache: {}, // { [projectId]: [tradie, ...] }
      projectTradiesLoading: {}, // { [projectId]: true/false }
      massNotificationTab: 0, // 0 = tradies, 1 = project
      companyTradies: [],
      companyTradiesMeta: null,
      companyTradiesLoading: false,
      companyTradiesLoadingMore: false,
      companyTradiesSelectAllBusy: false,
      tradieApiPerPage: 50,
      projectTradiesApiPerPage: 50,
      tradieSearch: '',
      companyProjects: [],
      companyProjectsLoading: false,
      projectSearch: '',
      massNotificationSelectedUserIds: [],
      massNotificationAttachments: [],

      projectPage: 1,
      perPage: 10,
      projectTradiesMeta: {},
      tradiesOnProjectPage: {},

      previews: [],
      form: {
        files: [],
      },
      mainProps: {
        center: true,
        width: 80,
      },
      previewProps: {
        center: true,
        blank: true,
        blankColor: '#bbb',
      },
      state: {
        files: null,
      },
      errors: {
        files: null,
      },
      loading: false,
      initialDataLoading: false,
      batchRecipientEndpointChecked: false,
      hasBatchRecipientEndpoint: false,
      suppressTradieSearchFetch: false,
    }
  },
  watch: {
    projectSearch() {
      this.projectPage = 1
    },
    tradieSearch() {
      if (!this.isVisible || this.suppressTradieSearchFetch) return
      this.fetchCompanyTradiesFirstPage()
    },
    tradieOnProjectSearch() {
      this.tradiesOnProject = 1
    },
  },

  computed: {
    // 1) Get user and company from Vuex
    ...mapState('auth', {
      authUser: (state) => state.user,
    }),
    ...mapState('company', {
      company: (state) => state.company,
    }),
    ...mapState('project', {
      projects: (state) => state.projects,
    }),

    ...mapGetters('company', {
      companyCanSendMassNotification: 'canSendMassNotification',
    }),
    ...mapGetters('auth', ['hasPermission']),

    // 2) Build the prefix
    companyTitlePrefix() {
      const name =
        (this.company && this.company.name) ||
        (this.authUser && this.authUser.company_name) ||
        (this.authUser &&
          this.authUser.company &&
          (typeof this.authUser.company === 'string'
            ? this.authUser.company
            : this.authUser.company.name))
      return name ? `${name}: ` : ''
    },

    isVisible: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },

    projectOptionsForMass() {
      return (this.projects || []).map((p) => ({
        value: p.id,
        text: p.name || `Project ${p.id}`,
      }))
    },

    /** Unique user IDs selected individually (across any project); per-project so same user in 2 projects counts once for sending */
    individuallySelectedUserIds() {
      const set = new Set()
      Object.values(this.massNotificationSelectedByProject || {}).forEach(
        (ids) => ids.forEach((id) => set.add(id)),
      )
      return [...set]
    },

    massNotificationRecipientCount() {
      if (!this.activeTabIsProjects) {
        return this.massNotificationSelectedUserIds.length
      }
      return this.individuallySelectedUserIds.length
    },

    selectedRecipientCount() {
      return this.activeTabIsProjects
        ? this.massNotificationProjectIds.length
        : this.massNotificationSelectedUserIds.length
    },

    canSendMassNotification() {
      const hasContent =
        this.massNotificationTitle.trim() && this.massNotificationBody.trim()

      const hasRecipients = this.activeTabIsProjects
        ? this.massNotificationProjectIds.length > 0 ||
          this.individuallySelectedUserIds.length > 0
        : this.massNotificationSelectedUserIds.length > 0

      return hasContent && hasRecipients
    },

    allTradiesSelected() {
      const ids = this.sortedCompanyTradies.map((t) => t.id)
      if (!ids.length) return false
      return ids.every((id) =>
        this.massNotificationSelectedUserIds.includes(id),
      )
    },

    someTradiesSelected() {
      const ids = this.sortedCompanyTradies.map((t) => t.id)
      if (!ids.length) return false
      return ids.some((id) => this.massNotificationSelectedUserIds.includes(id))
    },

    companyTradiesHasMore() {
      const m = this.companyTradiesMeta
      if (!m || m.last_page == null || m.current_page == null) return false
      return m.current_page < m.last_page
    },

    sortedCompanyTradies() {
      return [...(this.companyTradies || [])].sort((a, b) => {
        if (a.can_access && !b.can_access) return -1
        if (!a.can_access && b.can_access) return 1
        return 0
      })
    },

    filteredProjects() {
      if (!this.projectSearch) return this.projects || []
      const q = this.projectSearch.toLowerCase()
      return (this.projects || []).filter((p) =>
        (p.name || '').toLowerCase().includes(q),
      )
    },

    // Projects pagination
    paginatedProjects() {
      const start = (this.projectPage - 1) * this.perPage
      const end = start + this.perPage
      return this.filteredProjects.slice(start, end)
    },
    projectRows() {
      return this.filteredProjects.length
    },

    showTradiesTab() {
      return this.hasPermission('tradies.view')
    },
    showProjectsTab() {
      return this.hasPermission('projects.view')
    },
    // When the Tradies tab is hidden, Projects shifts to index 0.
    activeTabIsProjects() {
      if (!this.showTradiesTab && this.showProjectsTab) {
        return this.massNotificationTab === 0
      }
      return this.massNotificationTab === 1
    },

    isLocked() {
      if (this.companyCanSendMassNotification === true) return false
      if (this.companyCanSendMassNotification === false) return true
      if (this.authUser && this.authUser.subscription_status === 'trialing')
        return false
      return true
    },

    isLoadingAccess() {
      return this.companyCanSendMassNotification === null
    },

    showInitialSpinner() {
      return this.isLoadingAccess || this.initialDataLoading
    },
  },

  methods: {
    ...mapActions('project', {
      getProjects: 'getProjects',
    }),

    async onMassNotificationModalShow() {
      this.initialDataLoading = true
      this.resetStateExcludingProjectCache()
      this.$store.commit('company/setCanSendMassNotification', null)
      await this.$store.dispatch('company/fetchCanSendMassNotification')
      try {
        const fetches = []
        if (this.showProjectsTab) {
          await this.getProjects({ limit: 100 })
          fetches.push(this.fetchMassNotificationRecipientsSnapshot())
        }
        if (this.showTradiesTab) {
          fetches.push(this.fetchCompanyTradiesFirstPage())
        }
        await Promise.all(fetches)
      } finally {
        this.initialDataLoading = false
      }
    },

    onMassNotificationModalHidden() {
      this.resetState()
    },

    normalizeTradies(tradies) {
      return [...tradies].sort((a, b) => {
        if (a.can_access && !b.can_access) return -1
        if (!a.can_access && b.can_access) return 1
        return 0
      })
    },

    async fetchMassNotificationRecipientsSnapshot() {
      if (this.batchRecipientEndpointChecked) return

      this.batchRecipientEndpointChecked = true
      try {
        const { data } = await Api.get(
          'notifications/mass/recipients?limit=500&page=1&search=&archived_only=false',
        )
        const payload = Array.isArray(data && data.data) ? data.data : []

        payload.forEach((project) => {
          const projectId = project && project.id
          if (!projectId) return
          const tradies =
            project && project.tradies && Array.isArray(project.tradies.data)
              ? project.tradies.data
              : Array.isArray(project.tradies)
              ? project.tradies
              : []
          this.$set(
            this.projectTradiesCache,
            projectId,
            this.normalizeTradies(tradies),
          )
          this.$set(this.projectTradiesLoading, projectId, false)
        })
        this.hasBatchRecipientEndpoint = true
      } catch (e) {
        this.hasBatchRecipientEndpoint = false
      }
    },

    resetState() {
      this.massNotificationProjectIds = []
      this.massNotificationSelectedProjectId = null
      this.massNotificationSelectedByProject = {}
      this.massNotificationBody = ''
      this.massNotificationError = null
      this.massNotificationTitle = this.companyTitlePrefix
      this.projectTradiesCache = {}
      this.projectTradiesLoading = {}
      this.massNotificationTab = 0
      this.companyTradies = []
      this.companyTradiesMeta = null
      this.companyTradiesLoading = true
      this.companyTradiesLoadingMore = false
      this.companyTradiesSelectAllBusy = false
      this.projectSearch = ''
      this.tradieSearch = ''
      this.projectPage = 1
      this.projectTradiesMeta = {}
      this.companyProjects = []
      this.companyProjectsLoading = false
      this.initialDataLoading = false
      this.massNotificationSelectedUserIds = []
      this.previews = []
      this.form = { files: [] }
      this.state = { files: null }
      this.errors = { files: null }
    },

    resetStateExcludingProjectCache() {
      this.suppressTradieSearchFetch = true
      this.massNotificationProjectIds = []
      this.massNotificationSelectedProjectId = null
      this.massNotificationSelectedByProject = {}
      this.massNotificationBody = ''
      this.massNotificationError = null
      this.massNotificationTitle = this.companyTitlePrefix
      this.massNotificationTab = 0
      this.companyTradies = []
      this.companyTradiesMeta = null
      this.companyTradiesLoading = true
      this.companyTradiesLoadingMore = false
      this.companyTradiesSelectAllBusy = false
      this.projectSearch = ''
      this.tradieSearch = ''
      this.projectPage = 1
      this.companyProjects = []
      this.companyProjectsLoading = false
      this.massNotificationSelectedUserIds = []
      this.previews = []
      this.form = { files: [] }
      this.state = { files: null }
      this.errors = { files: null }
      // do NOT clear projectTradiesCache or projectTradiesLoading
      this.$nextTick(() => {
        this.suppressTradieSearchFetch = false
      })
    },

    projectTradiesForProject(projectId) {
      return this.projectTradiesCache[projectId] || []
    },

    // Returns true only once tradies are loaded for a project AND none have a token
    projectHasNoTokenUsers(projectId) {
      const cache = this.projectTradiesCache[projectId]
      if (!cache) return false // unknown yet; avoid blocking selection
      return !cache.some((t) => t.can_access)
    },

    async onProjectExpand(project) {
      const id = project.id
      if (this.projectTradiesCache[id]) return
      await this.fetchProjectTradiesPage(id, 1, false)
    },

    async fetchProjectTradiesPage(projectId, page, append) {
      this.$set(this.projectTradiesLoading, projectId, true)
      try {
        const { data } = await Api.get(
          `projects/${projectId}?limit=${this.projectTradiesApiPerPage}&page=${page}`,
        )
        const raw = data && data.data
        const tradies =
          raw &&
          raw.tradies &&
          (Array.isArray(raw.tradies.data) ? raw.tradies.data : [])
        const meta =
          raw && raw.tradies && raw.tradies.meta ? raw.tradies.meta : null
        this.$set(this.projectTradiesMeta, projectId, meta)
        if (append) {
          const cur = this.projectTradiesCache[projectId] || []
          const ids = new Set(cur.map((t) => t.id))
          const merged = [...cur]
          tradies.forEach((t) => {
            if (!ids.has(t.id)) merged.push(t)
          })
          this.$set(
            this.projectTradiesCache,
            projectId,
            this.normalizeTradies(merged),
          )
        } else {
          this.$set(
            this.projectTradiesCache,
            projectId,
            this.normalizeTradies(tradies),
          )
        }
      } catch (e) {
        if (!append) this.$set(this.projectTradiesCache, projectId, [])
      } finally {
        this.$set(this.projectTradiesLoading, projectId, false)
      }
    },

    projectTradiesHasMorePages(projectId) {
      const m = this.projectTradiesMeta[projectId]
      if (!m || m.last_page == null || m.current_page == null) return false
      return m.current_page < m.last_page
    },

    async loadMoreProjectTradies(projectId) {
      if (!this.projectTradiesHasMorePages(projectId)) return
      const m = this.projectTradiesMeta[projectId]
      const nextPage = m.current_page + 1
      await this.fetchProjectTradiesPage(projectId, nextPage, true)
    },

    async ensureAllProjectTradiesLoaded(projectId) {
      while (this.projectTradiesHasMorePages(projectId)) {
        await this.loadMoreProjectTradies(projectId)
      }
    },

    async toggleProjectSelection(projectId, checked) {
      this.massNotificationError = null
      if (checked) {
        // Load tradies if not yet cached
        if (!this.projectTradiesCache[projectId]) {
          await this.onProjectExpand({ id: projectId })
        }

        await this.ensureAllProjectTradiesLoaded(projectId)

        const tradies = this.projectTradiesCache[projectId] || []
        const accessibleIds = tradies
          .filter((t) => t.can_access)
          .map((t) => t.id)

        if (accessibleIds.length === 0) {
          this.massNotificationError =
            'No tradies with tokens assigned to this project. Assign a token before sending notifications.'
          return
        }

        this.$set(
          this.massNotificationSelectedByProject,
          projectId,
          accessibleIds,
        )
        if (!this.massNotificationProjectIds.includes(projectId)) {
          this.massNotificationProjectIds = [
            ...this.massNotificationProjectIds,
            projectId,
          ]
        }
      } else {
        this.massNotificationProjectIds =
          this.massNotificationProjectIds.filter((id) => id !== projectId)
        this.$set(this.massNotificationSelectedByProject, projectId, [])
      }
    },

    toggleMassNotificationTradie(projectId, id, checked) {
      const ids = [...(this.massNotificationSelectedByProject[projectId] || [])]

      if (checked) {
        if (!ids.includes(id)) ids.push(id)
      } else {
        const idx = ids.indexOf(id)
        if (idx !== -1) ids.splice(idx, 1)
      }

      this.$set(this.massNotificationSelectedByProject, projectId, ids)

      // Keep the project-level checkbox in sync:
      // checked = all tokenised users in this project are selected
      const tradies = this.projectTradiesCache[projectId] || []
      const allAccessibleIds = tradies
        .filter((t) => t.can_access)
        .map((t) => t.id)
      const allSelected =
        allAccessibleIds.length > 0 &&
        allAccessibleIds.every((uid) => ids.includes(uid))
      const projectSelected =
        this.massNotificationProjectIds.includes(projectId)

      if (allSelected && !projectSelected) {
        this.massNotificationProjectIds = [
          ...this.massNotificationProjectIds,
          projectId,
        ]
      } else if (!allSelected && projectSelected) {
        this.massNotificationProjectIds =
          this.massNotificationProjectIds.filter((p) => p !== projectId)
      }
    },

    close() {
      this.$emit('input', false)
    },

    userIdsExcludingSelectedProjects() {
      const userIdsInSelectedProjects = new Set()
      this.massNotificationProjectIds.forEach((projectId) => {
        ;(this.projectTradiesForProject(projectId) || []).forEach((t) =>
          userIdsInSelectedProjects.add(t.id),
        )
      })
      return this.individuallySelectedUserIds.filter(
        (id) => !userIdsInSelectedProjects.has(id),
      )
    },

    toggleTradieSelection(userId, checked) {
      const ids = [...this.massNotificationSelectedUserIds]
      const idx = ids.indexOf(userId)
      if (checked && idx === -1) ids.push(userId)
      if (!checked && idx !== -1) ids.splice(idx, 1)
      this.massNotificationSelectedUserIds = ids
    },
    isTradieSelected(userId) {
      return this.massNotificationSelectedUserIds.includes(userId)
    },

    /** GET companies/tradies — same route as roster; minimal payload when for_mass_notification=1. */
    massNotificationTradiesUrl(page) {
      const search = encodeURIComponent(this.tradieSearch || '')
      return (
        `companies/tradies?limit=${this.tradieApiPerPage}` +
        `&page=${page}&search=${search}&archived_only=false&for_mass_notification=1`
      )
    },

    async fetchCompanyTradiesFirstPage() {
      this.companyTradiesLoading = true
      this.companyTradiesMeta = null
      try {
        const { data } = await Api.get(this.massNotificationTradiesUrl(1))
        this.companyTradies = Array.isArray(data.data) ? data.data : []
        this.companyTradiesMeta = data.meta || null
      } catch (e) {
        this.companyTradies = []
        this.companyTradiesMeta = null
      } finally {
        this.companyTradiesLoading = false
      }
    },

    async loadMoreCompanyTradies() {
      if (!this.companyTradiesHasMore || this.companyTradiesLoadingMore) return
      const m = this.companyTradiesMeta
      const nextPage = m && m.current_page != null ? m.current_page + 1 : 2
      this.companyTradiesLoadingMore = true
      try {
        const { data } = await Api.get(
          this.massNotificationTradiesUrl(nextPage),
        )
        const rows = Array.isArray(data.data) ? data.data : []
        this.companyTradiesMeta = data.meta || this.companyTradiesMeta
        const existingIds = new Set(this.companyTradies.map((t) => t.id))
        const merged = [...this.companyTradies]
        rows.forEach((t) => {
          if (!existingIds.has(t.id)) merged.push(t)
        })
        this.companyTradies = merged
      } catch (e) {
        /* keep existing list */
      } finally {
        this.companyTradiesLoadingMore = false
      }
    },

    async ensureAllCompanyTradiesLoadedForSearch() {
      while (this.companyTradiesHasMore) {
        await this.loadMoreCompanyTradies()
      }
    },

    async fetchCompanyProjects() {
      this.companyProjectsLoading = true
      try {
        const { data } = await Api.get(
          'projects?limit=500&page=1&search=&archived_only=false',
        )
        this.companyProjects = Array.isArray(data.data) ? data.data : []
      } catch (e) {
        this.companyProjects = []
      } finally {
        this.companyProjectsLoading = false
      }
    },

    async onSendMassNotification() {
      this.massNotificationError = null

      const title = this.massNotificationTitle.trim()
      const body = this.massNotificationBody.trim()

      if (!title || !body) {
        this.massNotificationError = 'Title and body are required.'
        return
      }

      const projectTabUserIds = [...this.individuallySelectedUserIds]
      const userIds = [...this.massNotificationSelectedUserIds]

      if (this.activeTabIsProjects && !projectTabUserIds.length) {
        this.massNotificationError =
          'Select at least one project or one or more users.'
        return
      }

      if (!this.activeTabIsProjects && !userIds.length) {
        this.massNotificationError =
          'Select at least one project or one or more users.'
        return
      }

      this.massNotificationSending = true
      try {
        // On the Projects tab, send project_ids for every fully-selected
        // project (only those where all accessible tradies are ticked land
        // in massNotificationProjectIds) so the API records the send against
        // the project — scope=project, project_id set — and the Message Log
        // shows it instead of "-". user_ids then carries only the
        // individually-picked tradies from partially-selected projects;
        // userIdsExcludingSelectedProjects() drops anyone already covered by
        // a project_id so they aren't notified twice. The Individuals tab has
        // no projects, so it stays user_ids-only as before.
        const sendProjectIds = this.activeTabIsProjects
          ? [...this.massNotificationProjectIds]
          : []
        const sendUserIds = this.activeTabIsProjects
          ? this.userIdsExcludingSelectedProjects()
          : [...this.massNotificationSelectedUserIds]
        const hasAttachments = this.form.files && this.form.files.length > 0

        if (hasAttachments) {
          const formData = new FormData()
          formData.append('title', title)
          formData.append('body', body)
          sendUserIds.forEach((id) => formData.append('user_ids[]', id))
          sendProjectIds.forEach((id) => formData.append('project_ids[]', id))

          this.form.files.forEach((file, i) => {
            let toAppend = file
            const isFile = file instanceof File && file.name
            const hasValidExt =
              isFile && /\.(jpe?g|png|pdf|csv|xlsx)$/i.test(file.name)

            // Only files that lost their real name/extension somewhere
            // upstream (e.g. the Compressor pipeline hands back a Blob, not
            // a named File) get this fallback rename — real CSV/XLSX/PDF/
            // image uploads already have a valid extension and skip it
            // entirely.
            if (!hasValidExt) {
              const ext =
                file.type === 'image/png'
                  ? 'png'
                  : file.type === 'image/jpeg' || file.type === 'image/jpg'
                  ? 'jpg'
                  : file.type === 'application/pdf'
                  ? 'pdf'
                  : file.type === 'text/csv' ||
                    file.type === 'application/vnd.ms-excel'
                  ? 'csv'
                  : file.type ===
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  ? 'xlsx'
                  : 'jpg'
              toAppend = new File([file], `attachment-${i}.${ext}`, {
                type: file.type || 'image/jpeg',
              })
            }
            formData.append('attachments[]', toAppend)
          })

          await Api.post('notifications/mass', formData)
        } else {
          // Only include each array when it's non-empty: the API rejects an
          // empty array (Laravel's filled() treats [] as present), so the
          // Individuals tab must omit project_ids, and a send to only a
          // fully-selected project must omit user_ids.
          const payload = { title, body }
          if (sendUserIds.length) {
            payload.user_ids = sendUserIds
          }
          if (sendProjectIds.length) {
            payload.project_ids = sendProjectIds
          }
          await Api.post('notifications/mass', payload)
        }

        // A file picked earlier in this session may have been rejected (too
        // large, or an unsupported type) and never made it into form.files —
        // errors.files is the only record of that once Send has moved on
        // without it, so surface it now rather than let the notification
        // look like it went out with every attachment the user picked.
        this.$emit('sent', { droppedAttachmentReason: this.errors.files })
        this.$root.$emit('refreshMessagesLog')
        this.close()
      } catch (err) {
        const status = err.response && err.response.status
        const data = err.response && err.response.data

        if (status === 403) {
          this.massNotificationError =
            'You are not allowed to send this notification.'
        } else if (status === 422 && data && data.errors) {
          const messages = Object.values(data.errors || {}).flat()
          this.massNotificationError = messages.length
            ? messages.join(' ')
            : data.message || 'Validation error'
        } else {
          this.massNotificationError =
            (data && data.message) || err.message || 'Something went wrong.'
        }
      } finally {
        this.massNotificationSending = false
      }
    },

    launchFilePicker() {
      this.$refs.filePicker.$el.childNodes[0].click()
    },

    loadPhotos(e) {
      let _this = this
      const photos = e.dataTransfer ? e.dataTransfer.files : e

      this.loading = true

      for (let i = 0; i < photos.length; i++) {
        const file = photos[i]

        // Extension first, not MIME type — file.type is unreliable for CSV
        // specifically (Windows reports it as text/csv, application/vnd.ms-excel,
        // or sometimes an empty string depending on what's set as the default
        // handler), but the extension is always there and is what the native
        // file-picker itself filtered on.
        const name = (file && file.name) || ''
        const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase()

        const isPdf = ext === 'pdf' || file.type === 'application/pdf'
        const isCsv =
          ext === 'csv' ||
          file.type === 'text/csv' ||
          file.type === 'application/vnd.ms-excel'
        const isXlsx =
          !isCsv &&
          (ext === 'xlsx' ||
            file.type ===
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
        const isImage =
          !isPdf &&
          !isCsv &&
          !isXlsx &&
          (['jpg', 'jpeg', 'png'].includes(ext) ||
            file.type === 'image/jpeg' ||
            file.type === 'image/png')

        if (!isPdf && !isCsv && !isXlsx && !isImage) {
          this.state.files = false
          this.errors.files =
            'Files must be an image (JPG, PNG), PDF, CSV, or XLSX'
          this.loading = false
          continue // skip this file, don't process it
        }

        // Mirrors the backend's per-type caps (SendMassNotificationRequest)
        // — UX only, catches an oversized file before wasting time/data
        // uploading it; the server enforces the real limit regardless.
        // CSV/XLSX stay tighter than images/PDF since XLSX is parsed
        // server-side elsewhere in the app (CsvTradieImport) — a bigger
        // allowance there only grows the zip-bomb/memory blast radius.
        const maxBytes = isPdf
          ? 50 * 1024 * 1024
          : isXlsx
          ? 15 * 1024 * 1024
          : isCsv
          ? 10 * 1024 * 1024
          : 30 * 1024 * 1024
        const typeLabel = isPdf
          ? 'PDF'
          : isXlsx
          ? 'XLSX'
          : isCsv
          ? 'CSV'
          : 'Image'
        if (file.size > maxBytes) {
          this.state.files = false
          this.errors.files = `${typeLabel} must not be greater than ${
            maxBytes / 1024 / 1024
          }MB.`
          this.loading = false
          continue // skip this file, don't process it
        }

        if (isPdf) {
          this.previews.push('pdf')
          this.form.files.push(file)
          this.loading = false
        } else if (isCsv) {
          this.previews.push('csv')
          this.form.files.push(file)
          this.loading = false
        } else if (isXlsx) {
          this.previews.push('xlsx')
          this.form.files.push(file)
          this.loading = false
        } else {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = (e) => {
            const base64Img = e.target.result
            this.previews.push(base64Img)

            if (file.size > 2000000) {
              const binaryImg = this.convertDataURIToBinary(base64Img)
              const blob = new Blob([binaryImg], { type: file.type })

              // eslint-disable-next-line
              new Compressor(blob, {
                quality: 0.2,
                success(result) {
                  _this.addCompressedImage(result)
                },
              })
            } else {
              this.addCompressedImage(file)
            }
          }
        }
      }
    },

    addCompressedImage(result) {
      this.form.files.push(result)
      this.loading = false
    },

    convertDataURIToBinary(dataURI) {
      const BASE64_MARKER = ';base64,'
      const base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
      const base64 = dataURI.substring(base64Index)
      const raw = window.atob(base64)
      const rawLength = raw.length
      var array = new Uint8Array(new ArrayBuffer(rawLength))

      for (let i = 0; i < rawLength; i++) {
        array[i] = raw.charCodeAt(i)
      }
      return array
    },

    onRemovePreview(index) {
      this.form.files.splice(index, 1)
      this.previews.splice(index, 1)
    },

    async toggleSelectAllTradies(checked) {
      this.companyTradiesSelectAllBusy = true
      try {
        await this.ensureAllCompanyTradiesLoadedForSearch()
        const ids = this.sortedCompanyTradies
          .filter((t) => t.can_access)
          .map((t) => t.id)
        if (!ids.length) return

        let selected = [...this.massNotificationSelectedUserIds]

        if (checked) {
          ids.forEach((id) => {
            if (!selected.includes(id)) selected.push(id)
          })
        } else {
          selected = selected.filter((id) => !ids.includes(id))
        }

        this.massNotificationSelectedUserIds = selected
      } finally {
        this.companyTradiesSelectAllBusy = false
      }
    },

    tradiesOnProject(projectId) {
      const startVal = this.tradiesOnProjectPage[projectId] || 1
      const start = (startVal - 1) * this.perPage
      const end = start + this.perPage
      const wantedProject = this.projectTradiesCache[projectId] || []
      return wantedProject.slice(start, end)
    },

    projectTradieRows(projectId) {
      return (this.projectTradiesCache[projectId] || []).length
    },

    onProjectTradiePage(projectId, page) {
      this.$set(this.tradiesOnProjectPage, projectId, page)
    },

    async prepareForOpen() {
      await this.getProjects({ limit: 100 })
      await this.fetchMassNotificationRecipientsSnapshot()
    },
  },
}
</script>

<style lang="scss" scoped>
.mass-notif-row {
  height: 2.5rem;
  border-top: 1px solid grey;
  min-height: 2.5rem;
  padding: 0;

  &--project {
    background-color: #006cff;
    color: #fff;
  }

  &--user {
    background-color: #e4e4e4;
    border-top: 1px solid #d4d4d4;
  }
}

.mass-notif-row {
  height: 2.5rem;
  border-top: 1px solid grey;
  min-height: 2.5rem;
  padding: 0;

  &--project {
    background-color: #006cff;
    color: #fff;
  }

  &--user {
    background-color: #ffffff; // was #e4e4e4
    border-top: 1px solid #ffffff;
  }
}

.mass-notif-checkbox,
.mass-notif-checkbox-placeholder {
  margin: 10px;
  width: 28px;
  min-width: 28px;
  margin-bottom: 15px;
}

.mass-notif-checkbox--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mass-notif-label {
  flex: 1;
  padding-left: 16px;
  text-align: left;
}

.mass-notif-toggle-btn {
  height: 100%;
  border: none;
  padding-left: 16px;
  padding-right: 16px;
  background: transparent !important;
  color: inherit !important;
  text-align: left;

  &:hover,
  &:focus {
    background: rgba(0, 0, 0, 0.1) !important;
    color: inherit !important;
    border: none;
    box-shadow: none;
  }
}

.mass-notif-chevron {
  transition: transform 0.2s ease;
}

::v-deep .no-outline:focus {
  box-shadow: none;
}

.remove-icon {
  position: absolute;
  right: 6px;
  top: 6px;
  cursor: pointer;
}

.preview-item {
  width: 100px;
  height: 100px;
}

.upload-border {
  width: 100%;
  max-width: inherit;
}

.mass-notif-select-all {
  //padding: 4px 0;
  // Keep checkbox and hint on one line
  white-space: nowrap;

  &__hint {
    font-size: 0.8rem;
    color: #6c757d; // light grey text
  }
}

.mass-notif-divider {
  margin: 4px 0 8px;
  border-top: 1px solid #dee2e6; // light grey line
}

.mass-notif-lock-overlay ::v-deep > .b-overlay {
  backdrop-filter: blur(1.5px);
}

::v-deep .b-tooltip .tooltip-inner {
  background-color: rgb(231, 231, 233);
  color: #383838;
  font-size: 1.07rem; /* default is ~0.75rem, increase to taste */
  padding: 8px 12px; /* default is ~3px 8px */
  max-width: 230px; /* optional: wider box if text wraps */
}

.lock-icon {
  width: 450px; /* makes icon smaller — adjust to taste */
  height: auto;
  margin-bottom: 6px; /* small gap between icon and text */
}
.mass-notif-lock-overlay h2 {
  margin-top: -100px; /* pulls heading closer to icon */
}

// Keep Cancel/Send on one row, spread evenly across the full width instead
// of wrapping or bunching together, matching the same treatment used for
// the other modals on this page.
// Width is set to 100% because this div sits inside BootstrapVue's own
// .modal-footer (which is itself justify-content: flex-end) — without it
// the div just hugs its own content and gets pushed to the right, so the
// flex-grow on the buttons below has no free width to actually grow into.
.mass-notification-footer {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  width: 100%;
  justify-content: flex-end;
}

@media (max-width: 575.98px) {
  .mass-notification-footer {
    justify-content: stretch;
  }

  .mass-notification-btn {
    height: 44px;
    padding: 4px 14px;
    font-size: 0.85rem;
    flex: 1 1 0;
    min-width: 0;
  }
}
</style>
